import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Wallet,
  Receipt,
  CircleCheck,
  Calendar,
  CalendarCheck,
  ExternalLink,
  HardHat,
  TriangleAlert,
} from 'lucide-react';
import { formatUsd, invoiceTotals, invoiceReconciliation } from '@/lib/jobsData';
import { getPhase, getPhaseProgressPct, PHASE_MAX_INDEX } from '@/lib/jobPhases';
import { translatePhase } from '@/lib/i18nLabels';

const SectionLabel = ({ children }) => (
  <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground mb-2">
    {children}
  </div>
);

const KpiTile = ({ Icon, label, value, tone = 'default', testId }) => {
  const toneColor = {
    default: 'text-foreground',
    success: 'text-[hsl(var(--success))]',
    primary: 'text-[hsl(var(--primary))]',
    warning: 'text-[hsl(var(--warning))]',
  }[tone];
  return (
    <div
      className="rounded-md border border-border bg-[hsl(var(--background))]/40 p-3"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        <Icon size={12} className="text-[hsl(var(--primary))]" />
        {label}
      </div>
      <div className={`mt-1 text-base font-semibold ${toneColor}`}>{value}</div>
    </div>
  );
};

const getInitials = (name) => {
  if (!name) return '??';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export const JobOverviewTab = ({ job, clients = [] }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const totals = invoiceTotals(job?.invoices || []);
  const phase = Number(job?.phase) || 0;
  const phaseInfo = getPhase(phase);
  const phasePct = getPhaseProgressPct(phase);
  const totalValue = (Number(job?.contract) || 0) + (Number(job?.co_total) || 0);
  const remaining = Math.max(totalValue - totals.paid, 0);
  const reconcile = invoiceReconciliation(job);

  // Resolve linked client (by client_id, fallback to name match)
  const linkedClient =
    clients.find((c) => c?.id === job?.client_id) ||
    clients.find(
      (c) =>
        (c?.name || '').trim().toLowerCase() ===
        (job?.client || '').trim().toLowerCase()
    );

  const handleViewClient = () => {
    if (linkedClient?.id) {
      navigate(`/dashboard/clients-leads?contactId=${linkedClient.id}`);
    } else {
      navigate('/dashboard/clients-leads');
    }
  };

  return (
    <div className="space-y-5" data-testid="job-overview-tab">
      {/* Invoice/contract reconciliation warning */}
      {reconcile.warn ? (
        <div
          className="flex items-start gap-2.5 rounded-md border bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30 p-3 text-xs"
          data-testid="overview-reconcile-warning"
        >
          <TriangleAlert size={15} className="mt-0.5 shrink-0" />
          <span>
            {reconcile.gap > 0
              ? t('jobs.overview.reconcile_under', { amount: formatUsd(reconcile.gap) })
              : t('jobs.overview.reconcile_over', { amount: formatUsd(Math.abs(reconcile.gap)) })}
          </span>
        </div>
      ) : null}

      {/* Top KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <KpiTile
          Icon={Wallet}
          label={t('jobs.overview.contract')}
          value={formatUsd(totalValue)}
          tone="primary"
          testId="overview-kpi-contract"
        />
        <KpiTile
          Icon={CircleCheck}
          label={t('jobs.overview.collected')}
          value={formatUsd(totals.paid)}
          tone="success"
          testId="overview-kpi-collected"
        />
        <KpiTile
          Icon={Receipt}
          label={t('jobs.overview.remaining')}
          value={formatUsd(remaining)}
          tone={remaining > 0 ? 'warning' : 'default'}
          testId="overview-kpi-remaining"
        />
      </div>

      {/* Phase progress */}
      <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <SectionLabel>{t('jobs.overview.phase_progress')}</SectionLabel>
              <div className="text-base font-semibold text-foreground">
                {translatePhase(t, phaseInfo.key)}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {phase} / {PHASE_MAX_INDEX}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('jobs.overview.complete')}
              </div>
              <div
                className="text-base font-semibold text-[hsl(var(--primary))]"
                data-testid="overview-phase-pct"
              >
                {phasePct}%
              </div>
            </div>
          </div>
          <Progress
            value={phasePct}
            className="h-1.5 mt-3 bg-[hsl(var(--muted))] [&>div]:bg-[hsl(var(--primary))]"
          />
        </CardContent>
      </Card>

      {/* Dates + job type */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiTile
          Icon={Calendar}
          label={t('jobs.overview.started')}
          value={job?.started || t('common.tbd')}
          testId="overview-started"
        />
        <KpiTile
          Icon={CalendarCheck}
          label={t('jobs.overview.est_completion')}
          value={job?.est_end || t('common.tbd')}
          testId="overview-est-end"
        />
        <KpiTile
          Icon={HardHat}
          label={t('jobs.overview.job_type')}
          value={(job?.job_type || 'fixed').toString().toUpperCase()}
          testId="overview-job-type"
        />
      </div>

      {/* Client info */}
      <Card
        className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
        data-testid="overview-client-card"
      >
        <CardContent className="p-4">
          <SectionLabel>{t('jobs.overview.client')}</SectionLabel>
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 border border-[hsl(var(--primary))]/30">
              <AvatarFallback className="bg-[hsl(var(--muted))] text-[hsl(var(--primary))] text-xs font-semibold">
                {getInitials(job?.client)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-foreground truncate">
                {job?.client || t('jobs.overview.unlinked')}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {linkedClient?.email || linkedClient?.phone || job?.address || ''}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleViewClient}
              className="shrink-0 border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 uppercase tracking-[0.16em] text-xs"
              data-testid="overview-view-client"
            >
              <ExternalLink size={14} className="mr-1.5" />
              {t('jobs.overview.view_profile')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JobOverviewTab;
